"use client";

import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@v1/ui/button";
import { Sheet, SheetClose, SheetContent, SheetTrigger } from "@v1/ui/sheet";
import { cn } from "@v1/ui/utils";
import { Menu, X } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";

const NAV_LINKS = [
  { href: "#capabilities", label: "Features" },
  { href: "/blog", label: "Blog" },
  { href: "/about", label: "About" },
];

// Primary CTA opens the workflow-mapping dialog on legacy pages that still
// use this header rather than the editorial shell.
const CTA_HREF = "#join";

/**
 * Resolve a nav href against the current pathname so hash anchors keep
 * working when the user is on a non-home page (e.g. /blog).
 */
function resolveHref(href: string, pathname: string | null): string {
  if (!href.startsWith("#")) return href;
  return pathname === "/" ? href : `/${href}`;
}

// Routes that intentionally render without the global marketing header
// (e.g. private one-pagers/proposals, which have their own self-contained
// top strip and CTAs).
const HIDDEN_HEADER_ROUTE_PREFIXES = [
  "/",
  "/one",
  "/audere",
  "/hpt",
  "/proposals",
  "/about",
  "/support",
  "/privacy",
  "/terms",
  "/blog",
  "/research",
  "/pricing",
  "/concept",
] as const;

function isHeaderlessRoute(pathname: string | null): boolean {
  if (!pathname) return false;
  return HIDDEN_HEADER_ROUTE_PREFIXES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const pathname = usePathname();

  // Suppress the header entirely on routes that opt out (kept above all
  // other hooks/derived state so the early return is unambiguous).
  if (isHeaderlessRoute(pathname)) {
    return null;
  }

  // Routes whose hero (sitting under the absolutely-positioned header)
  // is the cinematic dark background. Everywhere else paints onto a light
  // page background, so the header switches to a dark-on-light treatment.
  const isOverDarkHero =
    pathname === "/" || pathname === "/blog" || pathname?.startsWith("/blog/");

  return (
    <header className="absolute top-0 left-0 right-0 z-50 py-5">
      <div className="mx-auto flex max-w-screen-xl items-center justify-between px-5 sm:px-8 md:px-12">
        <Link href="/" aria-label="MLX home" className="flex items-center">
          <span
            className={cn(
              "text-[26px] font-medium tracking-[-0.03em]",
              isOverDarkHero ? "text-white" : "text-foreground",
            )}
          >
            MLX
          </span>
        </Link>

        {/* Desktop navigation */}
        <nav className="hidden md:flex items-center gap-[35px]">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.href}
              href={resolveHref(link.href, pathname)}
              className={cn(
                "text-[15px] font-medium transition-colors",
                isOverDarkHero
                  ? "text-white/90 hover:text-white"
                  : "text-foreground/70 hover:text-foreground",
              )}
            >
              {link.label}
            </Link>
          ))}

          <ThemeToggle variant={isOverDarkHero ? "dark" : "light"} />

          {/* Primary action - glass effect on hero, solid outline elsewhere */}
          <Button
            asChild
            variant="outline"
            size="cta"
            className={cn(
              isOverDarkHero
                ? "border-white/20 bg-white/5 text-white backdrop-blur-sm hover:bg-white/10"
                : "border-border/60 bg-background text-foreground hover:bg-accent",
            )}
          >
            <Link href={resolveHref(CTA_HREF, pathname)}>Map a workflow</Link>
          </Button>
        </nav>

        {/* Mobile navigation */}
        <div className="flex md:hidden items-center gap-3">
          <ThemeToggle variant={isOverDarkHero ? "dark" : "light"} />

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className={cn(
                  "p-2 rounded-lg transition-colors",
                  isOverDarkHero
                    ? "text-white hover:bg-white/10"
                    : "text-foreground hover:bg-muted",
                )}
                aria-label="Open menu"
              >
                <Menu className="w-6 h-6" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[280px] bg-background border-border p-0"
            >
              {/* Mobile menu header */}
              <div className="flex items-center justify-between p-4 border-b border-border">
                <Link
                  href="/"
                  className="flex items-center"
                  onClick={() => setOpen(false)}
                >
                  <span className="text-foreground text-xl font-medium tracking-[-0.02em]">
                    MLX
                  </span>
                </Link>
                <SheetClose asChild>
                  <button
                    type="button"
                    className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                    aria-label="Close menu"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </SheetClose>
              </div>

              {/* Mobile nav links */}
              <nav className="flex flex-col p-4">
                {NAV_LINKS.map((link) => (
                  <Link
                    key={link.href}
                    href={resolveHref(link.href, pathname)}
                    onClick={() => setOpen(false)}
                    className="py-3 px-2 text-foreground hover:text-primary text-base font-medium transition-colors border-b border-border/50 last:border-b-0"
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>

              {/* Mobile CTA */}
              <div className="mt-auto p-4 border-t border-border">
                <Button
                  asChild
                  size="cta"
                  className="w-full bg-brand-cta text-white hover:bg-brand-cta-hover"
                >
                  <Link
                    href={resolveHref(CTA_HREF, pathname)}
                    onClick={() => setOpen(false)}
                  >
                    Map a workflow
                  </Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
